import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { formatInBothZones, getClientLabel, getAssistantLabel } from '@/utils/timezoneHelper';

const ServiceCalendar = ({ orders, onServiceClick }) => {
  const { t, i18n } = useTranslation();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [hoveredOrder, setHoveredOrder] = useState(null);
  const [viewMode, setViewMode] = useState('month');
  const [selectedDate, setSelectedDate] = useState(new Date());

  const locale = (i18n.language || 'es').split('-')[0];
  const monthNames = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) =>
      new Date(2025, i, 1).toLocaleString(locale, { month: 'long' })
    );
  }, [locale]);
  const dayNames = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) =>
      new Date(2025, 0, i).toLocaleString(locale, { weekday: 'short' })
    );
  }, [locale]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const getOrdersForDate = (date) => {
    return orders.filter(order => {
      // Mostrar todos los servicios, con o sin conductor asignado
      const orderDate = order.service_date || order.pickup_date_from;
      if (!orderDate) return false;
      const oDate = new Date(orderDate);
      return oDate.getDate() === date.getDate() &&
             oDate.getMonth() === date.getMonth() &&
             oDate.getFullYear() === date.getFullYear();
    });
  };

  const previousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentMonth);
  const today = new Date();

  // Create calendar grid
  const calendarDays = [];
  
  // Add empty cells for days before the first day of month
  for (let i = 0; i < startingDayOfWeek; i++) {
    calendarDays.push(<div key={`empty-${i}`} className="min-h-[80px] bg-gray-50"></div>);
  }

  // Add all days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, month, day);
    const dayOrders = getOrdersForDate(date);
    const isToday = date.toDateString() === today.toDateString();
    
    // Determine day position in week (0=Sunday, 6=Saturday)
    const dayOfWeek = date.getDay();
    // Show tooltip on left for Friday (5) and Saturday (6)
    const isRightEdge = dayOfWeek >= 5;

    calendarDays.push(
      <div 
        key={day} 
        className={`min-h-[80px] border border-gray-200 p-1 relative ${isToday ? 'bg-blue-50 border-blue-500' : 'bg-white hover:bg-gray-50'}`}
      >
        <div className={`text-sm font-semibold ${isToday ? 'text-blue-600' : 'text-gray-700'}`}>
          {day}
        </div>
        <div className="space-y-1 mt-1">
          {dayOrders.map((order, idx) => (
            <div key={idx} className="relative group">
              <div 
                className={`text-[10px] px-1 py-1 rounded truncate cursor-pointer transition-all hover:scale-105 ${
                  !order.driver_id ? 'bg-red-100 text-red-800 hover:bg-red-200 border border-red-300' :
                  order.status === 'assigned' || order.status === 'in_progress' || order.status === 'in_transit' ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200' :
                  order.status === 'completed' ? 'bg-green-100 text-green-800 hover:bg-green-200' :
                  'bg-gray-100 text-gray-800 hover:bg-gray-200'
                }`}
                onMouseEnter={() => setHoveredOrder(order.id)}
                onMouseLeave={() => setHoveredOrder(null)}
                onClick={() => onServiceClick && onServiceClick(order)}
              >
                {order.car_plate || order.id.substring(0, 4).toUpperCase()}
              </div>
              
              {/* Tooltip on hover - position changes based on day of week */}
              {hoveredOrder === order.id && (
                <div 
                  className={`absolute z-[9999] bg-gray-900 text-white text-[10px] p-3 rounded-lg shadow-2xl border border-gray-700 top-0 whitespace-normal ${
                    isRightEdge ? 'right-full mr-2' : 'left-full ml-2'
                  }`}
                  style={{
                    minWidth: '220px',
                    maxWidth: '280px'
                  }}
                  onMouseEnter={() => setHoveredOrder(order.id)}
                  onMouseLeave={() => setHoveredOrder(null)}
                >
                  <div className="space-y-2">
                    <div className="font-bold text-[#36e961] mb-1">
                      {order.car_brand} {order.car_model} ({order.car_plate})
                    </div>
                    <div>
                      <div className="font-bold text-green-400 mb-1 flex items-center gap-1">
                        <span>📍</span> {t('calendar_origin')}
                      </div>
                      <div className="pl-4 text-gray-200 leading-relaxed">{order.pickup_address}</div>
                    </div>
                    <div className="border-t border-gray-700 pt-2">
                      <div className="font-bold text-blue-400 mb-1 flex items-center gap-1">
                        <span>🎯</span> {t('calendar_destination')}
                      </div>
                      <div className="pl-4 text-gray-200 leading-relaxed">{order.delivery_address}</div>
                    </div>
                    <div className="border-t border-gray-700 pt-2 text-gray-300">
                      <div>{t('calendar_driver')} {order.driver_name || t('gps_live_na')}</div>
                      <div>{t('calendar_client')} {order.client_name || t('gps_live_na')}</div>
                      <div>{t('calendar_price')} €{order.total_price?.toFixed(2) || '0.00'}</div>
                      {(order.pickup_date_from || order.service_date) && order.pickup_time_start && (() => {
                        const dual = formatInBothZones(order.pickup_date_from || order.service_date, order.pickup_time_start);
                        if (!dual) return null;
                        return (
                          <div className="mt-1 border-t border-gray-600 pt-1 text-amber-200">
                            🕐 {getClientLabel()}: {dual.client} · {getAssistantLabel()}: {dual.assistant}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-3">
        <Button variant="outline" size="sm" onClick={previousMonth} className="h-8 px-3">
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <h3 className="text-lg font-semibold capitalize">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h3>
        <Button variant="outline" size="sm" onClick={nextMonth} className="h-8 px-3">
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mb-3 text-xs">
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-red-100 border border-red-300 rounded"></div>
          <span>{t('calendar_no_driver')}</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-yellow-100 rounded"></div>
          <span>{t('calendar_in_progress')}</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-green-100 rounded"></div>
          <span>{t('calendar_completed')}</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-gray-100 rounded"></div>
          <span>{t('calendar_others')}</span>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1 overflow-visible">
        {/* Day headers */}
        {dayNames.map((day, i) => (
          <div key={i} className="text-center text-sm font-semibold text-gray-600 py-2 bg-gray-50 rounded">
            {day}
          </div>
        ))}
        
        {/* Calendar days */}
        {calendarDays}
      </div>
    </div>
  );
};

export default ServiceCalendar;
